import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit} from "@angular/core";
import { cloneDeep } from "lodash-es";
import moment, { Moment } from "moment";
import {ITaxReturn} from "../model/i-tax-return";
import {TaxReturnsResolver} from "../../tax-returns-resolver";
import {TaxReturnStatus} from "../model/status";
import {AnswerAudit, AnswerFlag, AnswerWarning, Comment, Group, Question, Subcategory, Subgroup, TaxReturn, User} from "../../../../../swaggerServices";
import {Subject, Subscription} from "rxjs";
import { groupBy } from "lodash-es";
import {makeName} from "../../../../shared/util/makeName";
import { GlobalConfig } from "../../../config/config-states";
import { ConfigStateService } from "../../../config/config-state.service";
import { Tenant } from "../../../../shared/types/tenant.enum";
import { TenantConfigService } from "../../../../shared/services/tenant-config.service";
import { TRANSLOCO_SCOPE } from '@jsverse/transloco';

const dateFormat = "DD/MM/YYYY h:mm:ssa";

interface DateRange {
  start: Moment;
  end?: Moment;
}

interface AnswerValueChange {
  created: boolean;
  oldAnswer?: string;
  newAnswer?: string;
  changed?: boolean;
}

interface AnswerChange {
  answerId: number;
  parentAnswerId?: number;
  question: Question;
  answerValueChange?: AnswerValueChange;
  previousAnswerValue?: string;
  answerAudits: AnswerAudit[];
  warningsOpened: AnswerWarning[];
  warningsClosed: AnswerWarning[];
  warningsAffected: {[answerWarningId: string]: true};
  numWarningsAffected: number;
  flagsOpened: AnswerFlag[];
  flagsClosed: AnswerFlag[];
  flagComments: Comment[];
  flagsAffected: {[answerFlagId: string]: true};
  numFlagsAffected: number;
  comments: {commentId: number, comment: Comment};
  numComments: number;
}

interface SubgroupChange {
  subgroup: Subgroup;
  answerChanges: AnswerChange[];
  answerChangesByParentId?: {parentId: number, answerChanges: AnswerChange[]}[];
}

interface GroupChange {
  group: Group;
  subgroupChanges: SubgroupChange[];
}

interface SubcategoryChange {
  subcategory: Subcategory;
  groupChanges: GroupChange[];

  numComments: number;
  numWarningsOpened: number;
  numWarningsClosed: number;
  numWarningsAffected: number;
  numFlagsOpened: number;
  numFlagsClosed: number;
  numFlagComments: number;
  numFlagsAffected: number;
}

interface Snapshot {
  id: number;
  nextSnapshotId: number;
  startedByUser?: User;
  finishedByUser?: User;
  status: TaxReturnStatus;
  startDate: Moment;
  startDateString: string;
  endDate?: Moment;
  endDateString?: string;
  answerChanges: {[answerId: string]: AnswerChange};
  answerChangesArray: AnswerChange[];
  subcategoryChanges: SubcategoryChange[];
  numAnswersAdded: number;
  numAnswersUpdated: number;
  numComments: number;
  numWarningsOpened: number;
  numWarningsClosed: number;
  numFlagsOpened: number;
  numFlagsClosed: number;
  numFlagComments: number;
  changed: boolean;
  complexityPreparer: number;
  complexityReviewer1: number;
  complexityReviewer2: number;
}

interface SnapshotRange {
  title?: string;
  status?: TaxReturnStatus;
  snapshots: Snapshot[];
  users?: User[];
  startedByUser?: User;
  finishedByUser?: User;
  answerChangesArray: AnswerChange[];
  subcategoryChanges: SubcategoryChange[];
  startDateString: string;
  endDateString?: string;
  viewLink: {href: any, query: any};
  complexity: {title: string, complexity: number}[];
}

@Component({
    selector: 'app-change-history',
    templateUrl: './change-history.component.html',
    styleUrls: ['./change-history.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: { scope: 'tax-returns', alias: 'taxReturns' },
        }
    ],
    standalone: false
})
export class ChangeHistoryComponent implements OnInit, OnDestroy {
  taxReturn?: ITaxReturn;
  snapshots?: Snapshot[];
  selectedSnapshotRange?: SnapshotRange;
  firstSnapshot?: Snapshot;
  firstIndex?: number;
  lastIndex?: number;
  loadingSelectionSub?: Subscription;
  tenants: any;
  taxpayerAlias: string;
  subscriptions: Subscription[] = [];
  uiConfig: TenantConfigService;
  taxReturnAlias: string;


  constructor(
    private cdr: ChangeDetectorRef,
    public config: ConfigStateService,
    private taxReturnsResolver: TaxReturnsResolver) { }

  ngOnInit() {
    this.tenants = Tenant;
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.uiConfig = new TenantConfigService(config);
        this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
        this.taxpayerAlias = this.uiConfig.config.taxpayerAlias;
      })
    );

    this.subscriptions.push(this.taxReturnsResolver.getTaxReturnObs().subscribe((data: ITaxReturn) => {
      const start = Date.now();
      const tr = cloneDeep(data);

      tr.taxReturnSnapshots.sort((a, b) => a.id - b.id);
      Object.values(tr.answersById).forEach((answer: any) => {
        answer.answerAudits.sort((a, b) => a.id - b.id);
      });

      const snapshots = [];
      for (let i = 0; i < tr.taxReturnSnapshots.length; i++) {
        const ss = tr.taxReturnSnapshots[i];
        const nextSs = tr.taxReturnSnapshots[i + 1];
        const startDate = moment(ss.createdAt);
        const endDate = nextSs ? moment(nextSs.createdAt) : null;
        snapshots.push({
          id: ss.id,
          nextSnapshotId: nextSs ? nextSs.id : null,
          startedByUser: ss.user,
          finishedByUser: nextSs ? nextSs.user : null,
          status: TaxReturnStatus.byId(ss.newStatus, GlobalConfig.tenant),
          startDate,
          startDateString: startDate.format(dateFormat),
          endDate,
          endDateString: endDate ? endDate.format(dateFormat) : null,
          // Complexity based off the next snapshot value, just like endDate.
          // For the current status, take complexity from the tax return
          complexityPreparer: nextSs ? nextSs.complexityPreparer : tr.complexityPreparer,
          complexityReviewer1: nextSs ? nextSs.complexityReviewer1 : tr.complexityReviewer1,
          complexityReviewer2: nextSs ? nextSs.complexityReviewer2 : tr.complexityReviewer2,
        });
      }

      const dateRanges = snapshots.map(ss => ({start: ss.startDate, end: ss.endDate}));
      const changes = this.computeChanges(tr, dateRanges);
      changes.forEach(({answerChanges, subcategoryChanges}, i) => {
        const ss = snapshots[i];
        ss.subcategoryChanges = subcategoryChanges;
        ss.answerChanges = answerChanges;
        ss.answerChangesArray = Object.values(answerChanges);
        const {
          answersAdded, answersUpdated,
          comments,
          warningsOpened, warningsClosed, warningsAffected,
          flagsOpened, flagsClosed, flagComments, flagsAffected
        } = this.computeStatsForAnswerChanges(ss.answerChangesArray);
        ss.numAnswersAdded = answersAdded;
        ss.numAnswersUpdated = answersUpdated;
        ss.numComments = comments;
        ss.numWarningsOpened = warningsOpened;
        ss.numWarningsClosed = warningsClosed;
        ss.numWarningsAffected = warningsAffected;
        ss.numFlagsOpened = flagsOpened;
        ss.numFlagsClosed = flagsClosed;
        ss.numFlagComments = flagComments;
        ss.numFlagsAffected = flagsAffected;
        ss.changed = (answersAdded + answersUpdated + comments + warningsAffected + flagsAffected) > 0;
      });

      this.taxReturn = tr;
      this.snapshots = snapshots.filter((ss, index) => {
        //hide unchanged oneteam response
        if (!ss?.changed && ss?.status?.id === TaxReturnStatus?.ONE_TEAM_RESPONSE?.id) {
          return false
        //hide unchanged preparation which comes directly after a response from OT with no activity
        }else if (
          !ss?.changed &&
          ss?.status?.id === TaxReturnStatus?.IN_PREPARATION?.id &&
          !snapshots[index -1]?.changed &&
          snapshots[index -1]?.status?.id === TaxReturnStatus?.ONE_TEAM_RESPONSE?.id
        ) {
          return false
        }
        return true
      })

      const end = Date.now();

      this.cdr.detectChanges();
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  makeViewLink(snapshots: Snapshot[], subcategory?: Subcategory) {
    const lastSnapshot = snapshots[snapshots.length - 1];
    return {
      href: ['/tax-returns', this.taxReturn.id].concat(subcategory ? ['subcategories', subcategory.id] : []),
      query: lastSnapshot.nextSnapshotId != null ? {snapshot: lastSnapshot.nextSnapshotId} : null,
    };
  }

  selectSnapshotRangeAsync(taxReturn: ITaxReturn, snapshots: Snapshot[], first: Snapshot, last: Snapshot, firstIndex: number, lastIndex: number) {
    const subject = new Subject<SnapshotRange>();
    const dateRanges = [{start: first.startDate, end: last.endDate}];

    setTimeout(() => {
      const {answerChanges, subcategoryChanges} = this.computeChanges(taxReturn, dateRanges)[0];
      const users = {};
      for (const ss of snapshots) {
        if (ss.startedByUser) {
          users[ss.startedByUser.id] = ss.startedByUser;
        }
        if (ss.finishedByUser) {
          users[ss.finishedByUser.id] = ss.finishedByUser;
        }
      }
      subject.next({
        title: `Viewing changes across ${lastIndex - firstIndex + 1} snapshots`,
        status: null,
        snapshots: [first, last],
        users: Object.values(users),
        answerChangesArray: Object.values(answerChanges),
        subcategoryChanges,
        startDateString: first.startDateString,
        endDateString: last.endDateString,
        viewLink: this.makeViewLink([first, last]),
        complexity: [],
      });
    }, 0);

    return subject;
  }

  selectSnapshot(event: MouseEvent, snapshot: Snapshot) {
    // clear text selection when doing shift-click
    document.getSelection().removeAllRanges();

    const makeSingleSelection = () => ({
      title: null,
      status: snapshot.status,
      snapshots: [snapshot],
      users: [],
      startedByUser: snapshot.startedByUser,
      finishedByUser: snapshot.finishedByUser,
      answerChangesArray: snapshot.answerChangesArray,
      subcategoryChanges: snapshot.subcategoryChanges,
      startDateString: snapshot.startDateString,
      endDateString: snapshot.endDateString,
      viewLink: this.makeViewLink([snapshot]),
      complexity: snapshot.complexityPreparer > 0 && snapshot.complexityReviewer1 > 0 && snapshot.complexityReviewer2 > 0 ? [
        {title: "Preparer", complexity: snapshot.complexityPreparer},
        {title: "Reviewer 1", complexity: snapshot.complexityReviewer1},
        {title: "Reviewer 2", complexity: snapshot.complexityReviewer2},
      ] : [],
    });

    if (event.shiftKey && this.firstSnapshot && this.firstSnapshot !== snapshot) {
      const idx1 = this.snapshots.indexOf(this.firstSnapshot);
      const idx2 = this.snapshots.indexOf(snapshot);
      const firstIndex = Math.min(idx1, idx2);
      const lastIndex = Math.max(idx1, idx2);
      const first = this.snapshots[firstIndex];
      const last = this.snapshots[lastIndex];

      if (this.loadingSelectionSub) {
        this.loadingSelectionSub.unsubscribe();
      }
      // computing the snapshot range is slow, so put up a loading screen
      this.loadingSelectionSub = this.selectSnapshotRangeAsync(
        this.taxReturn,
        this.snapshots.filter((ss, i) => i >= firstIndex && i <= lastIndex),
        first,
        last,
        firstIndex,
        lastIndex
      ).subscribe(selectedSnapshotRange => {
        this.selectedSnapshotRange = selectedSnapshotRange;
        this.loadingSelectionSub = null;
        this.cdr.detectChanges();
      });

      this.firstIndex = firstIndex;
      this.lastIndex = lastIndex;
    } else {
      this.firstSnapshot = snapshot;
      this.selectedSnapshotRange = makeSingleSelection();

      const idx = this.snapshots.indexOf(snapshot);
      this.firstIndex = idx;
      this.lastIndex = idx;
    }
  }

  computeChanges(taxReturn: ITaxReturn, ranges: DateRange[]) {
    const getOrCreateAnswerChange = (answerChanges, answer) => {
      let answerChange = answerChanges[answer.id];
      if (answerChange == null) {
        answerChange = {
          answerId: answer.id,
          parentAnswerId: answer.parentAnswerId,
          question: taxReturn.questionsById[answer.questionId],
          answerAudits: [],
          warningsOpened: [],
          warningsClosed: [],
          warningsAffected: {},
          numWarningsAffected: 0,
          flagsOpened: [],
          flagsClosed: [],
          flagComments: [],
          flagsAffected: {},
          numFlagsAffected: 0,
          comments: {},
        };
        answerChanges[answer.id] = answerChange;
      }
      return answerChange;
    };

    const answerChangesByRange = ranges.map(() => ({}));
    Object.keys(taxReturn.answersById).forEach(answerId => {
      const answer = taxReturn.answersById[answerId];
      const question = taxReturn.questionsById[answer.questionId];
      if (question.isRowId || question.dataType === "table") {
        return; // skip
      }
      answer.answerAudits.forEach(answerAudit => {
        answerAudit.createdAtMoment = moment(answerAudit.createdAt);
        for (let i = 0; i < ranges.length; i++) {
          const dateRange = ranges[i];
          const answerChanges = answerChangesByRange[i];

          if (answerAudit.createdAtMoment.isBefore(dateRange.start)) {
            const answerChange = getOrCreateAnswerChange(answerChanges, answer);
            answerChange.previousAnswerValue = answerAudit.newAnswer;
          }

          if (answerAudit.createdAtMoment.isSameOrAfter(dateRange.start) &&
            (dateRange.end == null || answerAudit.createdAtMoment.isBefore(dateRange.end))) {

            const answerChange = getOrCreateAnswerChange(answerChanges, answer);
            if (answerChange.answerValueChange == null) {
              answerChange.answerValueChange = {
                created: answerAudit.typeOfChange === "INSERT",
                oldAnswer: answerAudit.oldAnswer || "",
                newAnswer: answerAudit.newAnswer || "",
              };
            }
            answerChange.answerAudits.push(answerAudit);
            answerChange.answerValueChange.newAnswer = answerAudit.newAnswer || "";
          }
        }
      });
      answer.comments.forEach(comment => {
        if (comment.answerWarningId != null) {
          return; // skip comments that are for warnings
        }

        const createdAtMoment = moment(comment.createdAt);

        for (let i = 0; i < ranges.length; i++) {
          const dateRange = ranges[i];
          const answerChanges = answerChangesByRange[i];

          if (createdAtMoment.isSameOrAfter(dateRange.start) &&
            (dateRange.end == null || createdAtMoment.isBefore(dateRange.end))) {
            const answerChange = getOrCreateAnswerChange(answerChanges, answer);

            if (comment.answerFlagId != null) {
              answerChange.flagComments.push(comment);
            } else if (comment.answerWarningId == null ) {
              answerChange.comments[comment.id] = comment;
            }
          }
        }
      });
      answer.answerWarnings.forEach(answerWarning => {
        const createdAtMoment = moment(answerWarning.createdAt);
        const closedAtMoment = answerWarning.closedAt ? moment(answerWarning.closedAt) : null;
        const status = answerWarning.status;

        for (let i = 0; i < ranges.length; i++) {
          const dateRange = ranges[i];
          const answerChanges = answerChangesByRange[i];

          if (createdAtMoment.isSameOrAfter(dateRange.start) &&
            (dateRange.end == null || createdAtMoment.isBefore(dateRange.end))) {
            const answerChange = getOrCreateAnswerChange(answerChanges, answer);
            answerChange.warningsOpened.push(answerWarning);
          }

          if (status !== 0 && closedAtMoment && closedAtMoment.isSameOrAfter(dateRange.start) &&
            (dateRange.end == null || closedAtMoment.isBefore(dateRange.end))) {
            const answerChange = getOrCreateAnswerChange(answerChanges, answer);
            answerChange.warningsClosed.push(answerWarning);
          }
        }
      });
      answer.answerFlags.forEach(answerFlag => {
        const createdAtMoment = moment(answerFlag.createdAt);
        const closedAtMoment = answerFlag.closedAt ? moment(answerFlag.closedAt) : null;
        const status = answerFlag.status;

        for (let i = 0; i < ranges.length; i++) {
          const dateRange = ranges[i];
          const answerChanges = answerChangesByRange[i];

          if (createdAtMoment.isSameOrAfter(dateRange.start) &&
            (dateRange.end == null || createdAtMoment.isBefore(dateRange.end))) {
            const answerChange = getOrCreateAnswerChange(answerChanges, answer);
            answerChange.flagsOpened.push(answerFlag);
          }

          if (status !== 0 && closedAtMoment && closedAtMoment.isSameOrAfter(dateRange.start) &&
            (dateRange.end == null || closedAtMoment.isBefore(dateRange.end))) {
            const answerChange = getOrCreateAnswerChange(answerChanges, answer);
            answerChange.flagsClosed.push(answerFlag);
          }
        }
      });
    });
    answerChangesByRange.forEach(answerChanges => {
      Object.values(answerChanges).forEach((answerChange: AnswerChange) => {
        answerChange.numComments = Object.keys(answerChange.comments).length;

        answerChange.flagsOpened.forEach(af => answerChange.flagsAffected[af.id] = true);
        answerChange.flagsClosed.forEach(af => answerChange.flagsAffected[af.id] = true);
        answerChange.flagComments.forEach((c: any) => answerChange.flagsAffected[c.answerFlagId] = true);
        answerChange.numFlagsAffected = Object.keys(answerChange.flagsAffected).length;

        answerChange.warningsOpened.forEach(aw => answerChange.warningsAffected[aw.id] = true);
        answerChange.warningsClosed.forEach(aw => answerChange.warningsAffected[aw.id] = true);
        answerChange.numWarningsAffected = Object.keys(answerChange.warningsAffected).length;
      });
    });
    // filter out answers created as empty, or answers that eventually came back to be the same value
    return answerChangesByRange.map(answerChanges => {
      const idsToDelete = new Set<string>();
      Object.keys(answerChanges).forEach(answerId => {
        const answerChange = answerChanges[answerId];
        const valueChanged = answerChange.answerAudits.some(aa => aa.userId != null) ||
          answerChange.answerValueChange != null && answerChange.answerValueChange.oldAnswer !== answerChange.answerValueChange.newAnswer;
        const flagsChanged = answerChange.flagsOpened.length || answerChange.flagsClosed.length || answerChange.flagComments.length;
        const warningsChanged = answerChange.warningsOpened.length || answerChange.warningsClosed.length;
        if (answerChange.numComments === 0 && !valueChanged && !flagsChanged && !warningsChanged) {
          idsToDelete.add(answerId);
        }
      });

      for (const answerId of Array.from(idsToDelete)) {
        delete answerChanges[answerId];
      }

      const subcategoryChanges = [];
      taxReturn.categories.forEach(c => {
        subcategoryChanges.push(...c.subcategories.map(sc => {
          const groupChanges = sc.groups.map(g => {
            const subgroupChanges = g.subgroups.map(sg => {
              const sgAnswerChanges = [];
              sg.questions.forEach(q => q.answers.forEach(a => {
                const answerChange = answerChanges[a.id];
                if (answerChange != null) {
                  sgAnswerChanges.push(answerChange);
                }
              }));
              const byParentId = groupBy(
                sgAnswerChanges.filter(ac => ac.parentAnswerId != null && ac.question.parentId != null),
                ac => ac.parentAnswerId
              );
              const answerChangesByParentId = Object.keys(byParentId).sort().map(parentId => ({
                parentId,
                answerChanges: byParentId[parentId],
              }));
              return {
                subgroup: sg,
                answerChanges: sgAnswerChanges,
                answerChangesByParentId: answerChangesByParentId.length ? answerChangesByParentId : null,
              };
            }).filter(sgc => sgc.answerChanges.length);
            return {group: g, subgroupChanges};
          }).filter(gc => gc.subgroupChanges.length);

          const allAnswerChanges = [];
          groupChanges.forEach(gc => gc.subgroupChanges.forEach(sgc => sgc.answerChanges.forEach(ac => {
            allAnswerChanges.push(ac);
          })));
          const {
            comments, warningsOpened, warningsClosed, warningsAffected, flagsOpened, flagsClosed, flagComments, flagsAffected,
          } = this.computeStatsForAnswerChanges(allAnswerChanges);

          return {
            subcategory: sc,
            groupChanges,
            numComments: comments,
            numWarningsOpened: warningsOpened,
            numWarningsClosed: warningsClosed,
            numWarningsAffected: warningsAffected,
            numFlagsOpened: flagsOpened,
            numFlagsClosed: flagsClosed,
            numFlagComments: flagComments,
            numFlagsAffected: flagsAffected,
          };
        }).filter(sc => sc.groupChanges.length));
      });

      return {
        answerChanges,
        subcategoryChanges,
      };
    });
  }

  computeStatsForAnswerChanges(answerChanges: AnswerChange[]) {
    let answersAdded = 0;
    let answersUpdated = 0;
    const comments = new Set();
    let warningsOpened = 0;
    let warningsClosed = 0;
    const warningsAffected = new Set();
    let flagsOpened = 0;
    let flagsClosed = 0;
    let flagComments = 0;
    const flagsAffected = new Set();
    answerChanges.forEach(answerChange => {
      if (answerChange.answerValueChange) {
        if (answerChange.answerValueChange.created) {
          answersAdded += 1;
        } else {
          answersUpdated += 1;
        }
      }
      Object.keys(answerChange.comments).forEach(cId => {
        comments.add(cId);
      });
      warningsOpened += answerChange.warningsOpened.length;
      warningsClosed += answerChange.warningsClosed.length;
      Object.keys(answerChange.warningsAffected).forEach(awId => {
        warningsAffected.add(awId);
      });
      flagsOpened += answerChange.flagsOpened.length;
      flagsClosed += answerChange.flagsClosed.length;
      flagComments += answerChange.flagComments.length;
      Object.keys(answerChange.flagsAffected).forEach(afId => {
        flagsAffected.add(afId);
      });
    });
    return {
      answersAdded, answersUpdated,
      comments: comments.size,
      warningsOpened, warningsClosed, warningsAffected: warningsAffected.size,
      flagsOpened, flagsClosed, flagComments, flagsAffected: flagsAffected.size,
    };
  }

  makeName(user: User) {
    return makeName(user);
  }
}
